import { Component, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { API_CONFIG } from 'src/app/config/api.config';
import { DIRETORIO } from 'src/app/config/diretorio.config';
import { ENTIDADE } from 'src/app/config/entidade.config';

import { ArquivoDTO } from 'src/app/models/arquivo/arquivoDTO';

import { ArquivoService } from 'src/app/services/arquivo/arquivo.service';
import { FunctionsService } from 'src/app/services/common/functions.service';
import { PermissaoAcessoService } from 'src/app/services/permissao-acesso/permissao-acesso.service';

@Component({
  selector: 'app-listar-arquivos',
  templateUrl: './listar-arquivos.page.html',
  styleUrls: ['./listar-arquivos.page.scss'],
})
export class ListarArquivosPage implements OnInit {

  canCreateEdit: boolean;
  canRemove: boolean;

  sortBy: string = 'id';
  sort: string = 'DESC';

  page = 0;
  pageSize = 10;
  totalPages = 0;
  totalElements = 0;

  carregando = false;

  arquivos: ArquivoDTO[] = [];

  filtro: string = '';
  mensagem: string = '';

  modalVisualizacaoAberto: boolean = false;

  arquivoVisualizacao: ArquivoDTO = null;
  arquivoTempVisualizacao: ArquivoDTO = null;

  arquivoEmVisualizacaoId: number = null;

  constructor(
    private arquivoService: ArquivoService,
    private functionsService: FunctionsService,
    private route: ActivatedRoute,
    private permissaoAcessoService: PermissaoAcessoService,
  ) { }

  ngOnInit() {
  }

  ionViewDidEnter() {
    // Verifica as permissões de acesso antes de carregar a listagem.
    this.checkAccessControl();

    this.findByFilter();
  }

  checkAccessControl() {
    this.permissaoAcessoService
      .canCreateAndEdit(window.location.href)
      .subscribe({
        next: (resp) => {
          this.canCreateEdit = resp;
        },
        error: () => {
          this.canCreateEdit = false;
        }
      });

    this.permissaoAcessoService
      .canRemove(window.location.href)
      .subscribe({
        next: (resp) => {
          this.canRemove = resp;
        },
        error: () => {
          this.canRemove = false;
        }
      });
  }

  /*
   * Limpa os critérios informados pelo usuário e restaura
   * a ordenação padrão da tela.
   */
  limparFiltros(): void {
    this.filtro = '';
    this.sortBy = 'id';
    this.sort = 'DESC';

    this.findByFilter();
  }

  /*
   * Reinicia a consulta quando o usuário altera o filtro.
   *
   * A paginação, a coleção atual e os totais são zerados para que
   * a primeira página da nova consulta substitua completamente
   * o resultado anterior.
   */
  findByFilter(): void {
    this.reiniciarConsulta();
  }

  /*
   * Reinicia a consulta quando o usuário altera a ordenação.
   *
   * A ordenação precisa ser executada no backend sobre todo o resultado,
   * e não apenas sobre os registros já carregados pelo navegador.
   */
  ordenarArquivos(): void {
    this.reiniciarConsulta();
  }

  /*
   * Centraliza o reinício de filtro e ordenação.
   *
   * Este método é utilizado sempre que precisamos abandonar o conjunto
   * paginado atual e iniciar novamente pela página zero.
   */
  private reiniciarConsulta(): void {
    if (this.carregando) {
      return;
    }

    this.functionsService.presentLoading();

    this.page = 0;
    this.totalPages = 0;
    this.totalElements = 0;

    this.arquivos = [];
    this.mensagem = '';

    this.carregarArquivos();
  }

  /*
   * Carrega uma página de arquivos.
   *
   * A primeira página é disparada pelas ações de consulta ou ordenação.
   * As páginas seguintes são carregadas pelo ion-infinite-scroll.
   *
   * Filtro, ordenação e paginação são processados no backend para que
   * totalElements e totalPages representem toda a consulta.
   */
  carregarArquivos(event?: any): void {
    if (
      this.carregando ||
      (this.totalPages > 0 && this.page >= this.totalPages)
    ) {
      if (event) {
        event.target.complete();
      }

      return;
    }

    /*
     * Quando não existe evento, a chamada veio de uma consulta principal
     * que abriu o loading global através de reiniciarConsulta().
     *
     * No infinite scroll usamos apenas o spinner próprio do componente.
     */
    const consultaPrincipal = !event;

    this.carregando = true;

    this.arquivoService.findPageByPath(
      ENTIDADE.REPOSITORIO,
      0,
      DIRETORIO.REPOSITORIO_ARQUIVOS,
      this.filtro,
      this.page,
      this.pageSize,
      this.sortBy,
      this.sort
    ).subscribe({
      next: resp => {
        const novosArquivos = resp.content ?? [];

        /*
         * Cada nova página é acrescentada à listagem existente.
         *
         * A coleção só é zerada quando a consulta é reiniciada.
         */
        this.arquivos.push(...novosArquivos);

        this.totalPages = resp.totalPages;
        this.totalElements = resp.totalElements;

        this.page++;

        /*
         * A mensagem utiliza totalElements retornado pelo backend.
         *
         * Portanto representa toda a consulta, e não apenas os arquivos
         * já carregados no navegador.
         */
        if (resp.first) {
          this.atualizarMensagemQuantidade();

          /*
           * Mantém o comportamento existente de trazer para o início
           * um arquivo recebido pela rota, caso ele esteja presente
           * na primeira página carregada.
           */
          const idArquivo =
            this.route.snapshot.params['idCurso'];

          if (idArquivo) {
            this.functionsService.moveItemToStart(
              this.arquivos,
              idArquivo
            );
          }
        }

        if (event) {
          event.target.complete();
        }

        this.carregando = false;

        if (consultaPrincipal) {
          this.functionsService.dismissLoading();
        }
      },

      error: error => {
        console.log(
          'Erro ao buscar arquivos:',
          error
        );

        this.mensagem =
          'Erro ao carregar arquivos.';

        if (event) {
          event.target.complete();
        }

        this.carregando = false;

        if (consultaPrincipal) {
          this.functionsService.dismissLoading();
        }
      }
    });
  }

  /*
   * Atualiza a mensagem do contexto da listagem a partir do total
   * retornado pelo backend.
   */
  private atualizarMensagemQuantidade(): void {
    if (this.totalElements === 0) {
      this.mensagem = 'Nenhum arquivo encontrado.';
    } else if (this.totalElements === 1) {
      this.mensagem = '1 arquivo encontrado.';
    } else {
      this.mensagem =
        `${this.totalElements} arquivos encontrados.`;
    }
  }

  /*
   * Prepara e abre a visualização somente quando solicitado.
   *
   * Arquivos Office continuam utilizando o arquivo temporário necessário
   * ao visualizador, porém a cópia ocorre exclusivamente neste momento.
   */
  async view(arquivo: ArquivoDTO) {
    if (!arquivo || this.arquivoEmVisualizacaoId !== null) {
      return;
    }

    this.arquivoEmVisualizacaoId = arquivo.id;

    try {
      // Garante que não exista arquivo temporário de uma visualização anterior.
      await this.removerArquivoTempVisualizacao();

      /*
       * Arquivos Office precisam ser copiados para o diretório temporário
       * antes de serem enviados ao visualizador externo.
       */
      if (this.isOfficeFile(arquivo.nome)) {

        const extensao =
          this.getExtensaoArquivo(arquivo.nome);

        const nomeDestino =
          `${Date.now()}_${arquivo.id}.${extensao}`;

        const respCP = await this.arquivoService
          .copyAndPaste(
            arquivo.path,
            arquivo.nome,
            '',
            0,
            this.getTipoArquivoPorExtensao(arquivo.nome),
            DIRETORIO.TEMP,
            nomeDestino
          )
          .toPromise();

        this.arquivoTempVisualizacao = respCP.body;

        arquivo.safePath =
          this.arquivoService.getSafeViewerPath(
            respCP.body
          );

      } else if (
        this.isImage(arquivo.nome) ||
        this.isPDF(arquivo.nome)
      ) {

        /*
         * Imagens e PDFs utilizam o caminho original.
         *
         * O safePath também só é calculado após a solicitação do usuário.
         */
        arquivo.safePath =
          this.arquivoService.getSafeViewerPath(
            arquivo
          );

      } else {

        // Tipos desconhecidos não possuem preview incorporado.
        arquivo.safePath = null;
      }

      /*
       * O modal só é aberto depois que o arquivo estiver preparado.
       *
       * Isso evita apresentar um modal vazio durante a preparação
       * de documentos Office.
       */
      this.arquivoVisualizacao = arquivo;
      this.modalVisualizacaoAberto = true;

    } catch (error) {

      console.log(
        'Erro ao preparar visualização do arquivo:',
        error
      );

      this.functionsService.showErrorAlert(
        'Atenção!',
        'Não foi possível carregar a visualização do arquivo.',
        error,
        ['Ok']
      );

    } finally {

      this.arquivoEmVisualizacaoId = null;
    }
  }

  /*
   * Solicita o fechamento do ion-modal.
   *
   * A limpeza efetiva ocorre no didDismiss para garantir que o iframe
   * já tenha sido desmontado antes da remoção do arquivo temporário.
   */
  fecharModalVisualizacao() {
    this.modalVisualizacaoAberto = false;
  }

  /*
   * Executado após o Ionic concluir o fechamento do modal.
   *
   * Nesse momento o conteúdo já deixou de utilizar o arquivo temporário.
   */
  async onModalVisualizacaoDidDismiss() {
    this.arquivoVisualizacao = null;

    await this.removerArquivoTempVisualizacao();
  }

  /*
   * Remove o arquivo temporário utilizado pelo visualizador Office.
   *
   * Apenas um documento pode permanecer aberto por vez, portanto uma
   * única referência temporária é suficiente.
   */
  private async removerArquivoTempVisualizacao(): Promise<void> {
    if (!this.arquivoTempVisualizacao) {
      return;
    }

    const arquivoTemp =
      this.arquivoTempVisualizacao;

    /*
     * Remove imediatamente a referência local.
     *
     * Assim, uma eventual falha no backend não deixa o componente
     * considerando o arquivo como ainda ativo.
     */
    this.arquivoTempVisualizacao = null;

    try {

      await this.arquivoService
        .deleteByPath(
          DIRETORIO.TEMP,
          arquivoTemp.nome
        )
        .toPromise();

    } catch (error) {

      /*
       * Uma falha na limpeza não pode impedir a continuidade do uso
       * da tela. O erro permanece registrado para diagnóstico.
       */
      console.log(
        'Erro ao excluir arquivo temporário:',
        arquivoTemp.path,
        arquivoTemp.nome,
        error
      );
    }
  }

  /*
   * Segurança adicional para o caso de o usuário abandonar a página
   * enquanto existir uma visualização ativa.
   */
  ionViewDidLeave() {
    this.modalVisualizacaoAberto = false;
    this.arquivoVisualizacao = null;

    this.removerArquivoTempVisualizacao();
  }

  getTipoArquivoPorExtensao(nome: string): string {
    const extensao =
      this.getExtensaoArquivo(nome);

    if (extensao === 'pdf') {
      return 'PDF';
    }

    if (
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
        .includes(extensao)
    ) {
      return 'IMAGEM';
    }

    if (['doc', 'docx'].includes(extensao)) {
      return 'DOCX';
    }

    if (['xls', 'xlsx'].includes(extensao)) {
      return 'XLSX';
    }

    if (['ppt', 'pptx'].includes(extensao)) {
      return 'PPTX';
    }

    return 'OUTRO';
  }

  getExtensaoArquivo(fileName: string): string {
    return fileName
      ? fileName.split('.').pop().toLowerCase()
      : '';
  }

  getFilePublicURL(
    fullPath: string,
    fileName: string
  ): string {

    if (!fullPath || !fileName) {
      return API_CONFIG.iisVirtualDirectoryURL +
        'empty_photo.jpg';
    }

    return this.arquivoService.getFilePublicURL(
      fullPath,
      fileName
    );
  }

  async delete(arquivo: ArquivoDTO) {

    const id = arquivo.id;

    const confirma =
      await this.functionsService.showConfirmAlert(
        'Confirma?',
        'Confirma a exclusão do arquivo ' +
          arquivo.nome +
          '?',
        'Confirmar',
        'Cancelar'
      );

    if (!confirma) {
      return;
    }

    this.functionsService.presentLoading();

    this.arquivoService.delete(id).subscribe({
      next: () => {

        const index =
          this.arquivos.indexOf(arquivo);

        if (index >= 0) {
          this.arquivos.splice(index, 1);
        }

        /*
         * totalElements representa o total real da consulta no backend.
         *
         * Não podemos utilizar arquivos.length porque, com paginação,
         * ele representa apenas a quantidade já carregada na tela.
         */
        if (this.totalElements > 0) {
          this.totalElements--;
        }

        this.atualizarMensagemQuantidade();

        /*
         * Após excluir o último item carregado de uma página, totalPages
         * pode ter mudado no backend.
         *
         * A próxima consulta ou carregamento reconstruirá naturalmente
         * os metadados de paginação.
         */
        this.functionsService.dismissLoading();
      },

      error: error => {

        this.functionsService.dismissLoading();

        this.functionsService.showErrorAlert(
          'Atenção!',
          'Ocorreu um erro ao executar a exclusão.',
          error,
          ['Ok']
        );
      }
    });
  }

  /*
   * Abre diretamente o arquivo em uma nova janela.
   *
   * Esta ação é independente do mecanismo de preview e não cria
   * arquivos temporários.
   */
  viewPublicFile(arquivo: ArquivoDTO) {

    const url = this.getFilePublicURL(
      arquivo.path,
      arquivo.nome
    );

    this.functionsService.goToPath(
      url,
      true
    );
  }

  isImage(nome: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(
      nome ?? ''
    );
  }

  isPDF(nome: string): boolean {
    return /\.pdf$/i.test(
      nome ?? ''
    );
  }

  isOfficeFile(nome: string): boolean {
    return /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(
      nome ?? ''
    );
  }

  isTextFile(nome: string): boolean {
    return /\.(txt|csv|log|xml|json)$/i.test(
      nome ?? ''
    );
  }

  isUnknownFile(nome: string): boolean {
    return !this.isImage(nome) &&
      !this.isPDF(nome) &&
      !this.isOfficeFile(nome);
  }

  getFileTypeIcon(nome: string): string {

    const extensao =
      this.getExtensaoArquivo(nome);

    if (extensao === 'pdf') {
      return 'document-text-outline';
    }

    if (['doc', 'docx'].includes(extensao)) {
      return 'document-text-outline';
    }

    if (['xls', 'xlsx'].includes(extensao)) {
      return 'grid-outline';
    }

    if (['ppt', 'pptx'].includes(extensao)) {
      return 'easel-outline';
    }

    if (
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
        .includes(extensao)
    ) {
      return 'image-outline';
    }

    if (
      ['txt', 'csv', 'log', 'xml', 'json']
        .includes(extensao)
    ) {
      return 'document-outline';
    }

    return 'attach-outline';
  }

  getFileTypeLabel(nome: string): string {

    const extensao =
      this.getExtensaoArquivo(nome);

    if (extensao === 'pdf') {
      return 'PDF';
    }

    if (['doc', 'docx'].includes(extensao)) {
      return 'Word';
    }

    if (['xls', 'xlsx'].includes(extensao)) {
      return 'Excel';
    }

    if (['ppt', 'pptx'].includes(extensao)) {
      return 'PowerPoint';
    }

    if (
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
        .includes(extensao)
    ) {
      return 'Imagem';
    }

    if (
      ['txt', 'csv', 'log', 'xml', 'json']
        .includes(extensao)
    ) {
      return 'Texto';
    }

    return extensao
      ? extensao.toUpperCase()
      : 'Arquivo';
  }

  getFileTypeClass(nome: string): string {

    const extensao =
      this.getExtensaoArquivo(nome);

    if (extensao === 'pdf') {
      return 'file-pdf';
    }

    if (['doc', 'docx'].includes(extensao)) {
      return 'file-word';
    }

    if (['xls', 'xlsx'].includes(extensao)) {
      return 'file-excel';
    }

    if (['ppt', 'pptx'].includes(extensao)) {
      return 'file-powerpoint';
    }

    if (
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
        .includes(extensao)
    ) {
      return 'file-image';
    }

    if (
      ['txt', 'csv', 'log', 'xml', 'json']
        .includes(extensao)
    ) {
      return 'file-text';
    }

    return 'file-other';
  }

  /*
   * Converte o tamanho armazenado em KB para a unidade mais adequada.
   */
  formatFileSize(kiloBytes: number): string {

    if (
      kiloBytes === null ||
      kiloBytes === undefined
    ) {
      return '-';
    }

    if (kiloBytes < 1024) {
      return `${this.formatNumber(kiloBytes)} KB`;
    }

    const megaBytes =
      kiloBytes / 1024;

    if (megaBytes < 1024) {
      return `${this.formatNumber(megaBytes)} MB`;
    }

    const gigaBytes =
      megaBytes / 1024;

    return `${this.formatNumber(gigaBytes)} GB`;
  }

  private formatNumber(valor: number): string {
    return valor.toLocaleString(
      'pt-BR',
      {
        maximumFractionDigits: 2
      }
    );
  }

  copyToClipboard(url: string): void {

    navigator.clipboard.writeText(url)
      .then(() => {

        this.functionsService.showAlert(
          'Copiado!',
          'URL copiada para a área de transferência!',
          ['Ok']
        );

      })
      .catch(error => {

        this.functionsService.showErrorAlert(
          'Erro!',
          'Não foi possível copiar a URL.',
          error,
          ['Ok']
        );
      });
  }
}